import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAsyncCallback } from '../../../../hooks'
import {
  EMAIL_CREATION_FORM_TEMPLATE_FOLDER_MASS,
  EmailCreationForm,
  EmailCreationFormProvider,
  isDirtyTableEmailCreationForm,
} from '../../../Email'
import { useResizeModal } from '../../../Modal'
import Text from '../../../Text'
import {
  useDocFlowContext,
  useDocFlowSendingContext,
} from '../../DocFlow.hooks'
import { getDocFlowSendingFilesByRecipientPayload } from '../../DocFlow.utils.files'
import { getDocFlowSendingFilesByRecipient } from '../DocFlowSending.api'
import { DocFlowSendingWidgetProcessFieldName } from '../DocFlowSending.constants'
import { DocFlowSendingEmailFormContainer } from '../DocFlowSendingEmailFormContainer'
import { DocFlowSendingWidgetContent } from '../DocFlowSendingWidgetContent'

import {
  useDocFlowEmailFormFieldsProps,
  useDocFlowEmailFormValues,
} from './DocFlowSendingEmailContent.hooks'
import {
  getDocFlowSendingEmailContentValidationSchema,
  getDocFlowSendingEmilContentNotificationMassMailing,
} from './DocFlowSendingEmailContent.utils'

import * as styles from './DocFlowSendingEmailContent.module.css'

function DocFlowSendingEmailContent() {
  const { documentId, phaseDisabled } = useDocFlowContext()

  const { processData } = useDocFlowSendingContext()

  const [modal, setModal] = useState(null)

  function handleCloseModal() {
    setModal(null)
  }

  const { emailAIDraftRef, emailBlackList, emailFormikRef, setEmailBlackList } =
    useDocFlowSendingContext()

  const { t } = useTranslation()

  const { initialValues } = useDocFlowEmailFormValues()

  const { fieldsProps, fileUploadLimitProps, formFieldsProps } =
    useDocFlowEmailFormFieldsProps()

  function handleAIModeChange(isDraftMode) {
    emailAIDraftRef.current = isDraftMode
  }

  const enrichedFormFieldsProps = {
    ...formFieldsProps,
    onAIModeChange: handleAIModeChange,
  }

  const { fullScreen, handleResizeModal } = useResizeModal()

  const handleGetFilesByRecipient = useAsyncCallback(
    id =>
      getDocFlowSendingFilesByRecipient({
        documentId,
        params: { recipient: id },
      }),
    [documentId]
  )

  const initialNotifications = [
    getDocFlowSendingEmilContentNotificationMassMailing(),
  ]

  const noSettings =
    processData?.[DocFlowSendingWidgetProcessFieldName.IsEmailDisabledForAll]

  return (
    <EmailCreationFormProvider
      massCheckboxDisabled
      PreviewButtonDelete={null}
      PreviewButtonSubmit={null}
      blackList={emailBlackList}
      blocked={phaseDisabled}
      externalRef={emailFormikRef}
      formBodyActionsProps={{
        ButtonCancel: false,
        ButtonSubmit: false,
      }}
      getFilesByRecipient={handleGetFilesByRecipient}
      getFilesByRecipientPayload={getDocFlowSendingFilesByRecipientPayload}
      getIsDirty={isDirtyTableEmailCreationForm}
      isFullScreen={fullScreen}
      setEmailBlackList={setEmailBlackList}
      setModal={setModal}
      onCloseModal={handleCloseModal}
      onResize={handleResizeModal}
    >
      <DocFlowSendingWidgetContent
        {...(noSettings ? { Title: null, className: styles.noSettings } : null)}
        title={t('DocumentStapling.EmailSettingsTitleKey')}
      >
        {noSettings ? (
          <Text isSecondary>
            {t('DocumentStapling.EmailSettingsNoSettingsTextKey')}
          </Text>
        ) : (
          <EmailCreationForm
            excludeAutoAddAdditionalContacts
            isMassMailing
            FormContainer={DocFlowSendingEmailFormContainer}
            containerProps={{
              modal,
            }}
            contentComponentProps={{
              className: styles.content,
              shadow: false,
            }}
            fieldsProps={fieldsProps}
            fileUploadLimitProps={fileUploadLimitProps}
            formFieldsProps={enrichedFormFieldsProps}
            getValidationSchema={getDocFlowSendingEmailContentValidationSchema}
            initialNotifications={initialNotifications}
            initialValues={initialValues}
            singleTemplateFolder={EMAIL_CREATION_FORM_TEMPLATE_FOLDER_MASS}
          />
        )}
      </DocFlowSendingWidgetContent>
    </EmailCreationFormProvider>
  )
}

export default DocFlowSendingEmailContent
